import { error, game as ccgame, warn } from 'cc';
import LZString from 'lz-string';
import Game from '../Game';
import ErrorConfigType from '../Config/ErrorConfigType';
import ErrorName from './ErrorName';
import { ActivityErrorDataType, ActivityEventName } from '../Config/ActivityConfigType';

export enum ErrorType {
    Error = '錯誤',
    Hint = '提示',
    Ignore = '忽略',
}

export type ErrorEntity = {
    name: string;
    code: string;
    type: ErrorType;
};

export default class ErrorHandler {
    protected readonly _defaultErrorEntity: ErrorEntity = null;
    protected _errorStackSkipLength = 2; // 前2個要剔除掉
    protected _errorStackRemainAmount = 5; // 最多保留5行堆疊資訊

    public constructor(
        protected game: Game,
        protected _errorConfig: ErrorConfigType,
        protected _firstError: boolean = true,
    ) {
        this._defaultErrorEntity = this.getDefaultErrorEntity();
        this.handleWebGLContextLost();
    }

    public handle(err: Error): void;
    public handle(err: string, nativeError?: Error | string | ErrorEvent | PromiseRejectionEvent): void;
    public handle(err: Error | string, nativeError?: Error | string | ErrorEvent | PromiseRejectionEvent): void {
        const errorEntity = this.getErrorEntity(err);
        if (errorEntity.type === ErrorType.Ignore) {
            return;
        }
        error(`handle error: ${errorEntity.name} code: ${errorEntity.code} type: ${errorEntity.type}`);
        error(err, nativeError);
        this.game.message.showError(errorEntity);
        if (errorEntity.type === ErrorType.Error) {
            // only report the first error
            if (this._firstError) {
                const errorReport = this.generateErrorReportData(errorEntity, err, nativeError);
                this.game.activity.send(ActivityEventName.Error, errorReport);
                this._firstError = false;
            }
            this.game.connection.disconnect();
        }
    }

    public reportError(err: Error | string, nativeError?: Error | string | ErrorEvent | PromiseRejectionEvent) {
        const errorEntity = {
            name: ErrorName.REPORT_ERROR_TO_ACTIVITY,
            code: '1900',
            type: ErrorType.Ignore,
        };
        const errorReport = this.generateErrorReportData(errorEntity, err, nativeError);
        this.game.activity.send(ActivityEventName.Error, errorReport);
    }

    public getErrorEntity(err: Error | string): Readonly<ErrorEntity> {
        if (err instanceof Error) {
            return this._defaultErrorEntity;
        }
        const result = this._errorConfig.find((entity) => entity.name === err || entity.code === err);
        if (result) {
            return result;
        }
        return this._defaultErrorEntity;
    }

    public cancelError(errorNames: string[]) {
        this.game.message?.closeErrorMessages(errorNames);
    }

    protected getDefaultErrorEntity(): ErrorEntity {
        return this._errorConfig.find((entity) => entity.name === ErrorName.FRONT_END_UNKNOWN_ERROR);
    }

    protected handleWebGLContextLost() {
        ccgame.canvas.addEventListener('webglcontextlost', (event) => {
            event.preventDefault();
            Game.activity.send(ActivityEventName.Error, {
                ErrorCode: 0,
                Message: `Lost WebGL context \n${JSON.stringify(event)}`,
                Stack: '',
                Native: '',
                LocationOrigin: window.location.origin,
            });
        });
    }

    /**
     * generate error report which will be sent to server to track the error
     * @param errorEntity error entity which is matched with the error
     * @param err error which is need to be handled
     * @param nativeError addtional error which is provided when {error} is a string to provide more information
     * @returns error report data
     */
    protected generateErrorReportData(
        errorEntity: ErrorEntity,
        err: Error | string,
        nativeError?: Error | string | ErrorEvent | PromiseRejectionEvent,
    ): ActivityErrorDataType {
        let stackString = '';
        try {
            if (nativeError instanceof ErrorEvent) {
                stackString = (nativeError?.error as Error)?.stack;
            } else if (nativeError instanceof PromiseRejectionEvent) {
                stackString = nativeError.reason.stack;
            }

            if (!stackString && err instanceof Error) {
                stackString = err.stack;
            }

            if (!stackString) {
                stackString = new Error().stack;
            }
            stackString = this.parseErrorStackUrl(stackString);
            stackString = this.clampStackString(stackString, errorEntity);
        } catch (e) {
            stackString = `Error stack is not available. ${e instanceof Error ? e.message : e}`;
            warn('Error stack is not available.', e);
        }
        const nativeString = (() => {
            nativeError ??= err;
            if (nativeError instanceof Error) {
                return nativeError.message;
            }
            if (nativeError instanceof ErrorEvent) {
                try {
                    return JSON.stringify({
                        type: nativeError.type,
                        message: nativeError.message,
                        bubbles: nativeError.bubbles,
                    });
                } catch (e) {
                    return `message: ${nativeError.message}`;
                }
            }
            if (nativeError instanceof PromiseRejectionEvent) {
                try {
                    const errorReason = nativeError.reason as Error;
                    return JSON.stringify({
                        name: errorReason?.name,
                        message: errorReason?.message,
                        stack: errorReason?.stack,
                    });
                } catch (e) {
                    return 'Unserializable promise rejection reason';
                }
            }
            if (typeof nativeError === 'string') {
                return nativeError;
            }
            try {
                return JSON.stringify(nativeError);
            } catch (e) {
                return `Unserializable Object: ${String(nativeError)}`;
            }
        })();
        const url = new URL(window.location.href);
        const { origin, pathname } = url;
        const fullPath = origin + pathname;
        return {
            ErrorCode: +errorEntity.code,
            Message: errorEntity.name,
            Stack: this.compressString(stackString),
            LocationOrigin: fullPath,
            Native: this.compressString(nativeString),
        };
    }

    protected clampStackString(stack: string, errorEntity: ErrorEntity): string {
        // don't truncate the stack string if the error is FRONT_END_UNKNOWN_ERROR (to better comprehend the unknown error)
        const url = new URL(window.location.href);
        const { origin, pathname } = url;
        const fullPath = origin + pathname;
        stack = stack.replaceAll(fullPath, '★');
        stack = stack.replaceAll(origin, '☆');
        if (errorEntity.name === ErrorName.FRONT_END_UNKNOWN_ERROR) {
            return stack;
        }
        return stack.substring(0, 600);
    }

    protected compressString(text: string): string {
        if (this.game.config.compressErrorStack) {
            return LZString.compress(text);
        }
        return text;
    }

    protected parseErrorStackUrl(stack: string): string {
        const result: string[] = [];
        const regex = /\(([^()]+)\)/g; // 匹配括號中的內容

        let match: RegExpExecArray | null;
        let i = 0;
        // eslint-disable-next-line no-cond-assign
        while ((match = regex.exec(stack)) !== null) {
            if (i < this._errorStackSkipLength) {
                i++;
                continue; // 前 {this._errorStackSkipLength} 行不處理
            }
            if (i >= this._errorStackSkipLength + this._errorStackRemainAmount) {
                break; // 後續超過 {this._errorStackRemainAmount} 行不再處理
            }
            // match[1] 就是括號裡面的內容
            result.push(match[1].trim());
            i++;
        }

        return result.join('\n');
    }
}
